import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import {
  DxCard,
  DxCardContent,
  DxCardHeader,
  DxList,
  DxNotificationToast,
  DxText,
} from '@dvag/design-system-react';

import i18next from 'i18next';

import { PersonInHouseholdDto } from 'graphql/person/generates';
import { handleNavigateToPath } from 'service/handleNavigateToPath';
import { SectionListData } from 'type/sectionList';
import { checkIcon } from 'utils/util';
import { dashboardUtil } from 'service/model/dashboardUtil';
import { Item } from 'component/BottomCards/Card/Item';
import { useGetHouseholdDashboard } from 'hooks/useGetHouseholdDashboard';
import './style.css';
import { dashboardItemLists } from './util';
import { isPersonYoungerThan18 } from '@dvag/dfs-ui-blocks/utils';
import { navigate as orchestratorNavigate } from '@dvag/dfs-orchestrator-client/messengers';
import { ConsentOrLegalGuardianModal } from 'component/ConsentOrLegalGuardianModal/ConsentOrLegalGuardianModal';
import { useGdprData } from '../../../../hooks/useGdprData';
import { pages } from '@dvag/dfs-constant-config';
import { useBottomCard } from '../../../../hooks/useBottomCard';

const CLOSE_SECONDS_TIMEOUT = 3;

const { PRIVACY_POLICY, ADVISOR, HOUSEHOLD_BUDGET_AND_CONTRACT, PENSION_AND_HOUSING } =
  dashboardUtil;

export const filterPersonsOlderThanEighteen = (
  personList: PersonInHouseholdDto[],
  includeSpecialAdults: boolean,
) =>
  personList.filter((person) => {
    const isOlderThan18 = !isPersonYoungerThan18(person.birthDate as string);
    const isSpecialAdult = !person.familyRelationship?.includes('KIND') && !person.birthDate;

    return includeSpecialAdults ? isOlderThan18 || isSpecialAdult : isOlderThan18;
  });

export const BottomCard = () => {
  const { householdId, section } = useParams();
  const navigate = useNavigate();
  const householdDashboard = useGetHouseholdDashboard();

  const pathToDashboard = `/haushalt/${householdId}/uebersicht`;

  const {
    chosenPerson,
    consentModalVisibility,
    hideConsentModal,
    isConsentEmailSent,
    personList,
    personListWithConsent,
    setChosenPerson,
    setConsentEmailSentStatus,
    showConsentModal,
  } = useBottomCard({ householdDashboard, section });

  const { disabledBottomList, bottomList, pensionAndHousingList, householdBudgetAndContractList } =
    dashboardItemLists(householdId, personList, personListWithConsent);

  const translation = {
    success: i18next.t('sendConsent.success'),
    essentials: i18next.t('dashboard.essentials'),
    dataProtection: i18next.t('sendConsent.dataProtection'),
    householdBudget: i18next.t('dashboard.bottomList.hhbAndContractOverview'),
    pensionAndHousing: i18next.t('dashboard.bottomList.pensionAndHousing'),
  };

  const cardTitle = {
    defaultSection: translation.essentials,
    [PRIVACY_POLICY]: translation.dataProtection,
    [HOUSEHOLD_BUDGET_AND_CONTRACT]: translation.householdBudget,
    [PENSION_AND_HOUSING]: translation.pensionAndHousing,
  };

  const gdprDataList = useGdprData(personList, householdDashboard?.data?.consentList);

  const list = {
    defaultSection: !householdId ? disabledBottomList : bottomList,
    [PRIVACY_POLICY]: gdprDataList,
    [HOUSEHOLD_BUDGET_AND_CONTRACT]: householdBudgetAndContractList,
    [PENSION_AND_HOUSING]: pensionAndHousingList,
  };

  const isSectionInItemList = !!section && Object.keys(list).includes(section);
  const sectionKey = isSectionInItemList ? section : 'defaultSection';
  const currentSection = list[sectionKey];

  const onItemClick = (path: string, id?: string | null, appName?: string) => {
    if (!householdId) {
      return;
    }

    if (path === `/haushalt/${householdId}/analyse/basisdaten/staatliche-rente`) {
      orchestratorNavigate(
        pages.PENSION.getUrl(householdId, true),
        pages.LIVING_SITUATION.getUrl(householdId, true),
      );
      return;
    }

    if (path === `/haushalt/${householdId}/analyse/basisdaten/wohnsituation`) {
      const backPath = `/berater/haushalt/${householdId}/uebersicht/sektion/${PENSION_AND_HOUSING}`;
      orchestratorNavigate(pages.LIVING_SITUATION.getUrl(householdId, true), backPath);
      return;
    }

    if (sectionKey === HOUSEHOLD_BUDGET_AND_CONTRACT || sectionKey === PENSION_AND_HOUSING) {
      return handleNavigateToPath(path, householdId, appName);
    }

    let targetPath = path;

    if (path === PRIVACY_POLICY) {
      if (id?.length) {
        const selectedPerson = personList?.find((person) => id === person.id);
        showConsentModal();
        setChosenPerson(selectedPerson);
      }
      targetPath = `${pathToDashboard}/sektion/${path}`;
    }

    if (path === HOUSEHOLD_BUDGET_AND_CONTRACT || path === PENSION_AND_HOUSING) {
      targetPath = `${pathToDashboard}/sektion/${path}`;
      return handleNavigateToPath(targetPath, '', ADVISOR);
    }

    return handleNavigateToPath(targetPath, householdId, appName);
  };

  const chosenPersonConsentStatus =
    !!chosenPerson &&
    householdDashboard?.data?.consentList?.find((data) => data?.personId === chosenPerson.id)
      ?.status;

  const consentModalProps = {
    onClosed: hideConsentModal,
    visible: consentModalVisibility,
    person: chosenPerson as PersonInHouseholdDto,
    isConsentFetching: householdDashboard?.isLoading || householdDashboard?.isRefetching,
    consent: householdDashboard?.data?.consentList?.find(
      (consent) => consent.personId === chosenPerson?.id,
    ),
    personConsentStatus: chosenPersonConsentStatus as string,
    onSubmitSuccess: hideConsentModal,
    personList: householdDashboard?.data?.personList || [],
    setIsConsentEmailSent: setConsentEmailSentStatus,
  };

  return (
    <DxCard style={{ overflow: 'hidden' }} data-testid="bottom-card-container">
      {chosenPerson && <ConsentOrLegalGuardianModal {...consentModalProps} />}
      <DxNotificationToast
        data-testid="consent-email-success"
        id="consent-email-success"
        visible={!!isConsentEmailSent}
        style={{ zIndex: 10000 }}
        icon={checkIcon('check-kreis')}
        onCloseNotification={() => {
          setConsentEmailSentStatus('');
        }}
        closeafter={CLOSE_SECONDS_TIMEOUT}
      >
        <div className="notification-text-container" data-testid="notification-success">
          <DxText>{translation.success}</DxText>
        </div>
      </DxNotificationToast>
      {isSectionInItemList ? (
        <DxCardHeader
          data-testid="navigate-back-to-essentials"
          label={cardTitle[sectionKey]}
          icon={checkIcon('pfeil-links')}
          iconcolor="default"
          onClick={() => navigate(pathToDashboard)}
          style={{ cursor: 'pointer' }}
        />
      ) : (
        <DxCardHeader label={cardTitle[sectionKey]} data-testid="title-container" />
      )}
      <DxCardContent className="dashboard-card-content">
        <div className="flex column">
          <DxList size="m">
            {currentSection?.map(({ title, icon, path, id, appName }, index) => (
              <Item
                id={id}
                key={id}
                onItemClick={() => onItemClick(path, currentSection?.[index]?.personId, appName)}
                title={title}
                cursorStyle={householdId ? 'pointer' : 'default'}
                icon={icon}
                renderRightSection={
                  (currentSection?.[index] as SectionListData)?.renderRightSection
                }
              />
            ))}
          </DxList>
        </div>
      </DxCardContent>
    </DxCard>
  );
};
